import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { formatCombo, effectiveCombo, useHotkeys, type HotkeyDef } from '../../../components/hotkeys';
import { useAuthStore } from '../../../auth/store';
import { fmtLocalDate } from '../../../utils/datetime';
import {
  fetchPlmBatches, fetchPlmNoSelfReview, PLM_PENDING_VALIDATION, type PlmBatchHeader,
} from './plm.api';
import { batchValidateBlock } from './plmWorkflow';
import { usePlmValidateActions } from './usePlmValidateActions';
import './plm.css';

/**
 * PLM Result Entry — the LIST view (standing rule 10: a nav entry opens a list
 * first, never a blank detail).  Every QC batch whose test is an
 * "Asbestos_PLM" unconventional analysis; clicking one opens its layers.
 *
 * Legacy has no equivalent screen — ALPACA reaches PLM only from the QC Batch
 * form's "PLM" button, which AI keeps too.  This list exists so the sidebar
 * entry has somewhere to land and an analyst can pick up the day's asbestos
 * batches directly.
 */

export interface PlmBatchListPageProps {
  /** Open one batch's PLM entry screen — in the list's own mode, unless `as`
   *  says otherwise (Edit always opens it for entry). */
  onOpen: (batchOid: string, as?: 'enter') => void;
  /** v13 mode (2026-09-28): View lists every batch; Enter only the batches
   *  still open for result entry; Validate (2026-09-29) only the batches
   *  Pending SDMS Validation, with Validate / Reject on the ticked rows. */
  mode?: 'view' | 'enter' | 'validate';
}

/** A batch's status as the list shows it: where it is in the v13 re-read flow
 *  while open, else its own workflow status. */
const plmBatchStatus = (r: PlmBatchHeader) =>
  r.editable ? (r.plmStage ?? 'In Analysis') : (r.statusText ?? '');

export default function PlmBatchListPage({ onOpen, mode = 'enter' }: PlmBatchListPageProps) {
  const validateMode = mode === 'validate';
  const [allRows, setRows]    = useState<PlmBatchHeader[]>([]);
  const rows = useMemo(() => (
    mode === 'enter' ? allRows.filter(r => r.editable)
      : validateMode ? allRows.filter(r => r.status === PLM_PENDING_VALIDATION)
        : allRows), [allRows, mode, validateMode]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Validate mode: the ticked batches (prototype v13 `MOD.sel`).
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());
  const me = useAuthStore(s => s.user?.id ?? null);
  const [noSelfReview, setNoSelfReview] = useState(false);
  const { validate, reject } = usePlmValidateActions();

  const load = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      setRows(await fetchPlmBatches());
      setTicked(new Set());
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - t0);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (validateMode) void fetchPlmNoSelfReview().then(setNoSelfReview); }, [validateMode]);

  // ── Validate mode actions (prototype v13 list: Validate · Reject on the
  //    ticked rows). The server re-checks every rule; the list only refuses up
  //    front what it can see — a batch the user is the first analyst of. ──
  const tickedRows = useMemo(() => rows.filter(r => ticked.has(r.batchOid)), [rows, ticked]);
  const ownBlock = tickedRows.map(r => batchValidateBlock(r, me, noSelfReview)).find(Boolean) ?? null;
  const targets = tickedRows.map(r => ({ id: r.batchOid, label: r.analyticalBatchId ?? r.batchOid }));
  const doValidate = useCallback(async () => {
    if (await validate(targets)) void load();
  }, [validate, targets, load]);
  const doReject = useCallback(async () => {
    if (await reject(targets)) void load();
  }, [reject, targets, load]);
  const hotkeys: HotkeyDef[] = validateMode ? [
    {
      combo: 'alt+v', label: 'Validate', group: 'PLM', order: 0,
      disabled: tickedRows.length === 0 || !!ownBlock || loading,
      run: () => { void doValidate(); },
    },
    {
      // Reject is the Rollback action's key (standing rule 14: Reject = Alt+B).
      combo: 'alt+b', label: 'Reject', group: 'PLM', order: 1,
      disabled: tickedRows.length === 0 || loading,
      run: () => { void doReject(); },
    },
  ] : [];
  useHotkeys(hotkeys);

  // Prototype v13 list columns (2026-09-28): QC Batch ID · Job ID · Test ·
  // First Analyst · Interdup Analyst · Date · Samples · Status. Status is the
  // v13 re-read stage while the batch is open (In Analysis · Pending Interdup ·
  // Interdup In Progress · Ready to Submit), then its own workflow status.
  const columns = useMemo<AppColumn<PlmBatchHeader>[]>(() => [
    { field: 'analyticalBatchId', header: 'QC Batch ID', width: 170,
      text: r => r.analyticalBatchId ?? '' },
    { field: 'jobIds', header: 'Job ID', width: 130, text: r => r.jobIds ?? '' },
    { field: 'test',   header: 'Test',   width: 140, text: r => r.test ?? '' },
    { field: 'firstAnalyst', header: 'First Analyst', width: 150, text: r => r.firstAnalyst ?? '' },
    { field: 'interdupAnalyst', header: 'Inter-dup Analyst', width: 150,
      text: r => r.interdupAnalyst ?? '—' },
    { field: 'createdDate', header: 'Date', width: 110,
      text: r => (r.createdDate ? fmtLocalDate(r.createdDate) : '') },
    { field: 'sampleCount', header: 'Samples', width: 90, align: 'right',
      text: r => String(r.sampleCount ?? 0) },
    { field: 'statusText', header: 'Status', width: 190, type: 'status', text: plmBatchStatus },
  ], []);

  return (
    <div className="plm-root">
      {/* Breadcrumb — the shared `dp-breadcrumb`, matching this form's sibling
          AnalysisListPage (Analysis Queue, Result Tracking) down to the plain
          non-link Home. This list had only the title, so the path showed on the
          entry screen but not here. Owner request 2026-09-28. */}
      <div className="dp-breadcrumb">
        <span>Home</span><i className="ti ti-chevron-right" />
        <span>Analysis</span><i className="ti ti-chevron-right" />
        <span className="dp-bc-active">PLM</span>
      </div>

      <FormTitle pageId="plm" loadMs={loadMs} loading={loading} auditFormName="PLM" />

      <AppToolbar
        left={validateMode ? (
          <>
            <button className="ca-btn ca-btn-solid" onClick={() => { void doValidate(); }}
              disabled={tickedRows.length === 0 || !!ownBlock || loading}
              title={ownBlock ?? (tickedRows.length === 0
                ? 'Tick the batches to validate'
                : `Validate the ticked batches (${formatCombo(effectiveCombo('alt+v'))})`)}>
              <i className="ti ti-circle-check" aria-hidden="true" /> Validate
            </button>
            <button className="ca-btn" onClick={() => { void doReject(); }}
              disabled={tickedRows.length === 0 || loading}
              title={tickedRows.length === 0
                ? 'Tick the batches to return to the analyst'
                : `Return the ticked batches to the analyst — reason required (${formatCombo(effectiveCombo('alt+b'))})`}>
              <i className="ti ti-arrow-back-up" aria-hidden="true" /> Reject
            </button>
          </>
        ) : undefined}
        onRefresh={() => { void load(); }}
        refreshing={loading}
        /* Edit is ALWAYS rendered and merely DISABLED until a row is picked
           (user request 2026-09-23).  <AppToolbar> only draws the button when
           `onEdit` is defined, so passing it conditionally made the button
           appear and disappear — the toolbar visibly reflowed as you clicked
           around.  A constant handler + `editDisabled` keeps the canonical
           toolbar shape (§18.6) steady. */
        /* Edit means "open it to work on it": it opens the batch in ENTER mode
           whatever the mode switch says (owner request 2026-10-08). Opening a
           row (double-click) still follows the mode switch. A submitted batch
           opened this way stays read-only until it is rolled back. Validate
           mode keeps its own review screen. */
        onEdit={() => { if (selected) onOpen(selected, validateMode ? undefined : 'enter'); }}
        editDisabled={!selected}
        exportData={{
          filename: 'PLM-Batches',
          headers: ['QC Batch ID', 'Job ID', 'Test', 'First Analyst', 'Inter-dup Analyst',
            'Date', 'Samples', 'Status'],
          rows: rows.map(r => [
            r.analyticalBatchId ?? '', r.jobIds ?? '', r.test ?? '', r.firstAnalyst ?? '',
            r.interdupAnalyst ?? '', r.createdDate ? fmtLocalDate(r.createdDate) : '',
            String(r.sampleCount ?? 0), plmBatchStatus(r),
          ]),
        }}
      />

      <div className="plm-list">
        <AppDataGrid
          rows={rows}
          columns={columns}
          getRowId={r => r.batchOid}
          selectedRowId={selected}
          onRowClick={r => setSelected(r.batchOid)}
          onRowDoubleClick={r => onOpen(r.batchOid)}
          selectable={validateMode}
          selectedIds={validateMode ? ticked : undefined}
          onSelectionChange={validateMode ? setTicked : undefined}
          loading={loading}
          defaultPageSize={20}
          emptyText={mode === 'enter'
            ? 'No PLM batch is open for result entry.'
            : validateMode
              ? 'No PLM batch is pending validation. A batch appears here once its Data Package is submitted.'
              : 'No asbestos PLM batches. A batch appears here once its test is configured as an Asbestos_PLM unconventional analysis.'}
        />
      </div>
      {validateMode && (
        <p className="plm-hint">
          Only batches pending validation. Validate / Reject the ticked rows here, or double-click
          to review a batch first. What follows validation is set by Default Settings.
          {noSelfReview ? ' You cannot validate a batch you analyzed.' : ''}
        </p>
      )}
    </div>
  );
}
